import type { ContentSummary, ContentType } from '@augment-toolkit/core';
import type { AgentSummary } from '../api/agents.api';

/**
 * Searching and filtering the catalog in the browser, over the cache the layout
 * already holds — see `docs/adr/0002-client-side-search.md`.
 *
 * **The counts are why this is one function rather than three.** A facet count
 * is not "how many records have this stage" but "how many results you would see
 * if you ticked this box", which depends on the query and on every *other*
 * filter group. Computing results and counts separately is how the sidebar
 * starts disagreeing with the list beside it.
 */

/**
 * What is ticked. **An empty group means no constraint**, not "nothing
 * selected" — which is what makes "Reset all" nothing more than emptying both,
 * and what stops a fresh page showing zero results.
 */
/**
 * The catalog's three record types, and the Agent Skills beside them. **Agent Skills are not a
 * `ContentType`**: that is the catalog's own taxonomy and lives in `core`, and an
 * Agent Skill is not a record in it — it comes from the Agent Skill library and has no stage of
 * its own, no path in the tree and no number.
 */
export type SearchType = ContentType | 'Agent';

export type SearchFilters = {
  /** API stage names — `Build`, not `BUILD`. */
  stages: readonly string[];
  types: readonly SearchType[];
};

export const NO_FILTERS: SearchFilters = { stages: [], types: [] };

/** One checkbox row. `count` is what ticking it would show. */
export type Facet = {
  /** The value written to the URL and matched against a record. */
  value: string;
  label: string;
  count: number;
  checked: boolean;
};

export type SearchResults = {
  items: ContentSummary[];
  /** The Agent Skills that match, drawn ahead of the catalog's results. */
  agents: AgentSummary[];
  stages: Facet[];
  types: Facet[];
  /** True when nothing has been asked yet — no query and no filter ticked. */
  isUnasked: boolean;
};

/**
 * The three record types, in taxonomy order, with the plural the sidebar draws.
 *
 * **The designs say `Methods / Processes / Practices` and this says
 * `Phases / Processes / Methods`.** There is no Practice in the content model,
 * so a Practices row could only ever read `0` — a promise rather than a filter.
 * Phases were missing from the design's list and are a real destination.
 */
const TYPE_LABELS: readonly { value: SearchType; label: string }[] = [
  { value: 'Phase', label: 'Phases' },
  { value: 'Process', label: 'Processes' },
  { value: 'Method', label: 'Methods' },
  { value: 'Agent', label: 'Agent Skills' },
];

/**
 * What a record is searched over.
 *
 * **The path is in there on purpose, with its hyphens opened out.** It carries
 * every ancestor's slug, so `user research` finds the methods beneath User
 * Research as well as the process itself — without it, a method sharing no word
 * with its process is unreachable from that process's name.
 */
const haystack = (item: ContentSummary): string =>
  `${item.name} ${item.description} ${item.path.replace(/-/g, ' ')}`.toLowerCase();

/** Split on whitespace; an all-whitespace query has no terms and matches everything. */
const termsOf = (query: string): string[] => query.toLowerCase().split(/\s+/).filter(Boolean);

/**
 * How well a record answers the query, for ordering only.
 *
 * **A name match beats a body match**, because someone typing "field study"
 * wants Field Study rather than the eleven methods that mention it. Beyond that
 * the catalog's own depth-first order wins, so results stay grouped by stage and
 * parent instead of shuffled by a relevance number nobody can see.
 */
const scoreOf = (displayName: string, query: string, terms: string[]): number => {
  const name = displayName.toLowerCase();
  const trimmed = query.trim().toLowerCase();

  if (name === trimmed) {
    return 3;
  }

  if (name.includes(trimmed)) {
    return 2;
  }

  return terms.every((term) => name.includes(term)) ? 1 : 0;
};

/**
 * Records matching the text query, best first. **Every term must appear
 * somewhere** — an AND rather than an OR, because a two-word query that returns
 * everything containing either word returns most of the catalog.
 */
const matchQuery = (catalog: readonly ContentSummary[], query: string): ContentSummary[] => {
  const terms = termsOf(query);

  if (terms.length === 0) {
    return [...catalog];
  }

  return catalog
    .filter((item) => {
      const text = haystack(item);
      return terms.every((term) => text.includes(term));
    })
    .map((item, index) => ({ item, index, score: scoreOf(item.name, query, terms) }))
    // Index breaks the tie, so the sort is stable across browsers and the
    // catalog's tree order survives inside each score band.
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((ranked) => ranked.item);
};

/**
 * What an Agent Skill is searched over: its title and summary, and **the names of the
 * methods it supports**, so searching for a method finds the Agent Skill that helps with
 * it. The Agent Skill's own name is there with its hyphens opened out, as a path is.
 */
const agentHaystack = (agent: AgentSummary): string =>
  `${agent.title} ${agent.summary} ${agent.name.replace(/-/g, ' ')} ${agent.methods
    .map((method) => method.name)
    .join(' ')}`.toLowerCase();

/** Agent Skills answering the query: every term, best title match first, library order within a band. */
const matchAgents = (agents: readonly AgentSummary[], query: string): AgentSummary[] => {
  const terms = termsOf(query);

  if (terms.length === 0) {
    return [...agents];
  }

  return agents
    .filter((agent) => {
      const text = agentHaystack(agent);
      return terms.every((term) => text.includes(term));
    })
    .map((agent, index) => ({ agent, index, score: scoreOf(agent.title, query, terms) }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((ranked) => ranked.agent);
};

/**
 * **An Agent Skill has no stage of its own; it borrows its methods'.** The stages it
 * is under are those of the catalog records it links to, and a link to a record
 * the catalog does not hold adds none.
 */
const stagesOfAgent = (agent: AgentSummary, stageById: ReadonlyMap<string, string>): Set<string> => {
  const stages = new Set<string>();

  for (const method of agent.methods) {
    const stage = stageById.get(method.id);

    if (stage !== undefined) {
      stages.add(stage);
    }
  }

  return stages;
};

const agentPassesTypes = (filters: SearchFilters): boolean =>
  filters.types.length === 0 || filters.types.includes('Agent');

const agentPassesStages = (stages: ReadonlySet<string>, filters: SearchFilters): boolean =>
  filters.stages.length === 0 || filters.stages.some((stage) => stages.has(stage));

const matchesStage = (item: ContentSummary, stages: readonly string[]): boolean =>
  stages.length === 0 || stages.includes(item.stage);

const matchesType = (item: ContentSummary, types: readonly SearchType[]): boolean =>
  types.length === 0 || types.includes(item.type);

/**
 * The stages the catalog actually contains, in taxonomy order.
 *
 * **Derived rather than listed, which departs from the designs.** The Figma
 * sidebar names four and the catalog has five — Delivery Leadership is real and
 * has records — so hardcoding four would leave every Delivery record searchable
 * and unfilterable, with the sidebar's counts failing to add up to the heading's.
 */
const stagesInCatalog = (catalog: readonly ContentSummary[]): { stage: string; stageNumber: number }[] => {
  const seen = new Map<string, number>();

  for (const item of catalog) {
    if (!seen.has(item.stage)) {
      seen.set(item.stage, item.stageNumber);
    }
  }

  return [...seen.entries()]
    .map(([stage, stageNumber]) => ({ stage, stageNumber }))
    .sort((a, b) => a.stageNumber - b.stageNumber);
};

/**
 * Search the catalog and count every facet, in one pass over the results.
 *
 * `nameFor` is passed in rather than imported so this file stays free of the
 * theme, like the rest of `content/`.
 */
export const runSearch = (
  catalog: readonly ContentSummary[],
  query: string,
  filters: SearchFilters,
  nameFor: (stage: string) => string,
  agents: readonly AgentSummary[] = [],
): SearchResults => {
  const matched = matchQuery(catalog, query);
  const matchedAgents = matchAgents(agents, query);
  const stageById = new Map(catalog.map((item) => [item.id, item.stage]));
  const agentStages = new Map(
    matchedAgents.map((agent) => [agent, stagesOfAgent(agent, stageById)]),
  );
  const visibleAgents = agentPassesTypes(filters)
    ? matchedAgents.filter((agent) => agentPassesStages(agentStages.get(agent) ?? new Set(), filters))
    : [];

  const items = matched.filter(
    (item) => matchesStage(item, filters.stages) && matchesType(item, filters.types),
  );

  // Each group is counted against the results filtered by the *other* group and
  // not by itself. Counting a group against itself prints 0 beside every
  // unticked box the moment anything is ticked, leaving no way to tell an empty
  // stage from a hidden one.
  const forStages = matched.filter((item) => matchesType(item, filters.types));
  const forTypes = matched.filter((item) => matchesStage(item, filters.stages));
  // Counted against the stage group like every other type.
  const agentsForTypes = matchedAgents.filter((agent) =>
    agentPassesStages(agentStages.get(agent) ?? new Set(), filters),
  ).length;
  // **An Agent Skill linked to methods in two stages counts in both**, as any item
  // shown under two filters does, so the stage counts can add up to more than
  // the total.
  const agentsForStage = (stage: string): number =>
    agentPassesTypes(filters)
      ? matchedAgents.filter((agent) => agentStages.get(agent)?.has(stage)).length
      : 0;

  const stages = stagesInCatalog(catalog).map(({ stage }) => ({
    value: stage,
    label: nameFor(stage),
    count: forStages.filter((item) => item.stage === stage).length + agentsForStage(stage),
    checked: filters.stages.includes(stage),
  }));

  const types = TYPE_LABELS
    // A type with nothing of it anywhere in the catalog is not drawn at all — a
    // permanently empty row is a claim about the taxonomy, not a filter.
    .filter(({ value }) =>
      value === 'Agent' ? agents.length > 0 : catalog.some((item) => item.type === value),
    )
    .map(({ value, label }) => ({
      value,
      label,
      count:
        value === 'Agent'
          ? agentsForTypes
          : forTypes.filter((item) => item.type === value).length,
      checked: filters.types.includes(value),
    }));

  return {
    items,
    agents: visibleAgents,
    stages,
    types,
    isUnasked:
      termsOf(query).length === 0 && filters.stages.length === 0 && filters.types.length === 0,
  };
};

/**
 * Ticking or unticking one box. **Returns a new array**, because the filters
 * live in the URL and the caller writes them straight back into it.
 */
export const toggleFacet = <T extends string>(selected: readonly T[], value: T): T[] =>
  selected.includes(value)
    ? selected.filter((entry) => entry !== value)
    : [...selected, value];
